import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { Card, CardBody, Input, Button, Link } from "@heroui/react";
import { Divider } from "@heroui/react";
import api from "../services/api";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useAuth } from "../contexts/auth-context";

interface AuthPageProps {
  onLogin: () => void;
}

export const AuthPage: React.FC<AuthPageProps> = ({ onLogin }) => {
  const location = useLocation();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [isYandexLoading, setIsYandexLoading] = React.useState(false);
  const [yandexError, setYandexError] = React.useState<string | null>(null);
  
  const { login /*, register */ } = useAuth();
  
  // Обработка входа
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(username, password);
      onLogin(); // Переход на страницу аккаунта после успешного входа
    } catch (err) {
      setError("Неверный логин или пароль. Пожалуйста, проверьте введенные данные.");
      console.error("Ошибка входа:", err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Получение демо-данных для разработки
  const getDemoCredentials = () => {
    setUsername("testuser");
    setPassword("password");
  };

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);

  // Запуск авторизации через Яндекс ID через backend API
  const handleYandexLogin = async () => {
    setYandexError(null);
    setIsYandexLoading(true);
    try {
      // Бэкенд возвращает authorize_url, куда нужно просто редиректнуть пользователя
      const response = await api.get("/users/auth/yandex/start/");
      const authorizeUrl = response.data?.authorize_url;

      if (!authorizeUrl) {
        throw new Error("Не удалось получить ссылку авторизации от сервера");
      }

      window.location.href = authorizeUrl;
    } catch (e: any) {
      console.error("Ошибка старта авторизации через Яндекс", e);
      setYandexError("Авторизация через Яндекс не удалась — попробуйте ещё раз.");
      setIsYandexLoading(false);
    }
  };

  return (
    <section className="py-24 bg-background">
      <Helmet>
        <title>Вход и регистрация в интернет-магазин — SAFONOVA</title>
        <meta name="description" content="Вход в личный кабинет Интернет-магазина SAFONOVA — управление заказами, профиль и история покупок. Регистрация нового пользователя." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/auth'}`} />
        <meta property="og:title" content="Вход и регистрация в Интернет-магазин — SAFONOVA" />
        <meta property="og:description" content="Вход в личный кабинет Интернет-магазина SAFONOVA — управление заказами, профиль и история покупок. Регистрация нового пользователя." />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/auth'}`} />
      </Helmet>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-md mx-auto"
        >
          <Card className="border border-divider">
            <CardBody className="p-8">
              <h1 className="text-2xl font-light text-center mb-6">
                Вход в <span className="font-medium">личный кабинет</span>
              </h1>
              
              {/* Отображаем ошибку, если есть */}
              {error && (
                <div className="bg-danger-50 text-danger p-3 mb-6 text-sm">
                  {error}
                </div>
              )}

              {yandexError && (
                <div className="bg-danger-50 text-danger p-3 mb-4 text-sm">
                  {yandexError}
                </div>
              )}
              
                <form onSubmit={handleLogin} className="space-y-4">
                  <Input
                    type="text"
                    label="Email"
                    placeholder="Введите ваш email"
                    value={username}
                    onValueChange={setUsername}
                    variant="bordered"
                    isRequired
                    autoComplete="username"
                  />
                  
                  <Input
                    type={isPasswordVisible ? "text" : "password"}
                    label="Пароль"
                    placeholder="Введите пароль"
                    value={password}
                    onValueChange={setPassword}
                    variant="bordered"
                    isRequired
                    autoComplete="current-password"
                    endContent={
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="focus:outline-none"
                      >
                        {isPasswordVisible ? (
                          <Icon icon="lucide:eye-off" className="text-foreground/60" width={16} />
                        ) : (
                          <Icon icon="lucide:eye" className="text-foreground/60" width={16} />
                        )}
                      </button>
                    }
                  />
                  
                  {/* <div className="flex justify-end">
                    <Button 
                      color="primary" 
                      variant="flat"
                      size="sm"
                      className="px-5"
                      as="a" 
                      href="#" // Добавить обработку для восстановления пароля
                    >
                      Забыли пароль?
                    </Button>
                  </div> */}
                  
                  <Button
                    type="submit"
                    color="primary"
                    className="w-full"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Вход..." : "Войти"}
                  </Button>
                  <p className="text-xs text-foreground/70 text-center mt-4">
                    Нет аккаунта?{' '}
                    <a href="/register" className="text-primary">Зарегистрируйтесь</a>
                  </p>
                </form>

                <div className="mt-6">

                  <Divider className="my-6" />

                  <div
                    id="yandex-auth-button-container"
                    className="flex justify-center"
                  >
                    <Button
                      variant="bordered"
                      className="w-full h-12 min-h-12 border-[rgba(180,184,204,0.28)] bg-[rgba(180,184,204,0.14)] hover:bg-[rgba(180,184,204,0.2)] hover:border-[rgba(180,184,204,0.28)]"
                      onPress={handleYandexLogin}
                      isLoading={isYandexLoading}
                    >
                      <span className="flex items-center gap-2 justify-center">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FC3F1D] text-white text-sm font-semibold">
                          Я
                        </span>
                        Войти через Яндекс ID
                      </span>
                    </Button>
                  </div>
                </div>
              
              {/* 
              
              <div className="space-y-3">
                <Button
                  startContent={<Icon icon="logos:google" width={18} />}
                  variant="bordered"
                  className="w-full"
                  onPress={() => {
                    // 🟢 ПРОДАКШН: раскомментируйте для использования OAuth
                    // window.location.href = '/api/auth/google'
                    alert("Авторизация через Google будет доступна в продакшн-версии");
                  }}
                >
                  Продолжить с Google
                </Button>
                
                <Button
                  startContent={<Icon icon="logos:vk" width={18} />}
                  variant="bordered"
                  className="w-full"
                  onPress={() => {
                    // 🟢 ПРОДАКШН: раскомментируйте для использования OAuth
                    // window.location.href = '/api/auth/vk'
                    alert("Авторизация через ВКонтакте будет доступна в продакшн-версии");
                  }}
                >
                  Продолжить с ВКонтакте
                </Button>
              </div> */}
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};